<template>
  <div class="programs-content">
    <!-- 왼쪽: 주간 목록(그 주에 쓴 프로그램과 수업은 있는데 아직 안 쓴 날짜·타입) -->
    <div class="panel" aria-label="운동 프로그램 주간 목록">
      <div class="panel-head">
        <h2>운동 프로그램</h2>
        <p class="panel-sub">
          날짜·타입마다 한 번 써요. 같은 날 같은 타입 수업은 시간이 달라도 이
          프로그램을 함께 써요.
        </p>
        <div class="week-navigation">
          <button
            class="nav-btn"
            type="button"
            :disabled="weekLoading"
            @click="moveWeek(-1)"
          >
            ← 이전 주
          </button>
          <span class="current-week">{{ weekRangeText }}</span>
          <button
            class="nav-btn"
            type="button"
            :disabled="weekLoading"
            @click="moveWeek(1)"
          >
            다음 주 →
          </button>
        </div>
        <p v-if="weekError" class="week-msg week-msg--error" role="alert">
          {{ weekError }}
          <button class="link-btn" type="button" @click="loadWeek">
            다시 불러오기
          </button>
        </p>
        <p v-else-if="classesPartial" class="week-msg">
          일부 날짜의 수업 목록을 불러오지 못해 그 날짜에는 안 쓴 프로그램이
          보이지 않아요.
        </p>
        <p v-else-if="!weekLoading && missing > 0" class="week-msg">
          수업은 있는데 아직 안 쓴 프로그램 {{ missing }}개
        </p>
      </div>

      <p v-if="weekLoading" class="empty">불러오는 중…</p>
      <div v-else-if="!weekError" class="days">
        <section
          v-for="row in weekRows"
          :key="row.date"
          class="day-card"
          :class="{ 'day-card--today': row.date === today }"
        >
          <h3 class="day-title">{{ row.label }}</h3>
          <ul v-if="row.entries.length" class="entries">
            <li v-for="e in row.entries" :key="e.type">
              <button
                class="entry"
                type="button"
                :class="{
                  selected: isSelected(row.date, e.type),
                  'entry--missing': e.status === 'missing',
                  'entry--legacy': e.status === 'legacy',
                }"
                :disabled="e.status === 'legacy'"
                :title="
                  e.status === 'legacy'
                    ? '예전 타입 수업이라 프로그램을 쓸 수 없어요. 회원 앱에는 기본 문장이 보여요.'
                    : null
                "
                @click="pickEntry(row.date, e.type)"
              >
                <span class="entry-top">
                  <!-- 쓴 프로그램은 카디오 포커스를 함께 "Pull & Cardio · 무산소 역치"(R10). null이면 회색 "카디오 포커스 없음"(R10 전 개발 DB 행) -->
                  <span class="entry-name">
                    <span
                      class="entry-type"
                      :class="'tone-' + toneOf(e.type)"
                      >{{ e.label }}</span
                    >
                    <template v-if="e.status === 'written'">
                      <span v-if="e.cardioFocus" class="entry-focus">{{
                        " · " + cardioLabelOf(e.cardioFocus)
                      }}</span>
                      <span v-else class="entry-focus entry-focus--none">{{
                        " · 카디오 포커스 없음"
                      }}</span>
                    </template>
                  </span>
                  <span v-if="e.classCount" class="entry-count"
                    >수업 {{ e.classCount }}개</span
                  >
                  <span
                    class="chip"
                    :class="
                      e.status === 'written'
                        ? 'chip--done'
                        : e.status === 'legacy'
                        ? 'chip--legacy'
                        : 'chip--missing'
                    "
                    >{{
                      e.status === "written"
                        ? "작성함"
                        : e.status === "legacy"
                        ? "예전 타입"
                        : "아직 안 썼어요"
                    }}</span
                  >
                </span>
                <span v-if="e.headline" class="entry-headline">{{
                  e.headline
                }}</span>
              </button>
            </li>
          </ul>
          <p v-else class="day-empty">
            {{ row.classesKnown ? "수업·프로그램 없음" : "프로그램 없음" }}
          </p>
        </section>
      </div>
    </div>

    <!-- 오른쪽: 쓰기. 날짜·타입을 고르면 그 프로그램을 불러온다 -->
    <aside class="right" aria-label="운동 프로그램 쓰기">
      <div class="editor">
        <h3 class="panel-title">프로그램 쓰기</h3>
        <div class="pickers">
          <label class="field">
            <span class="label">날짜</span>
            <input
              class="input"
              type="date"
              name="program-date"
              :value="form.date"
              :disabled="saving"
              @change="onPickDate($event)"
            />
          </label>
          <label class="field">
            <span class="label">타입</span>
            <select
              class="input"
              name="program-type"
              :value="form.type"
              :disabled="saving"
              @change="onPickType($event)"
            >
              <option value="" disabled>타입을 골라 주세요</option>
              <option v-for="t in typeOptions" :key="t.value" :value="t.value">
                {{ t.text }}
              </option>
            </select>
          </label>
        </div>

        <p v-if="!form.date || !form.type" class="editor-empty">
          날짜와 타입을 고르면 그 날짜·타입의 프로그램을 쓸 수 있어요.
        </p>
        <p v-else-if="loadingProgram" class="editor-empty">불러오는 중…</p>
        <p
          v-else-if="programLoadError"
          class="editor-empty editor-empty--error"
        >
          {{ programLoadError }}
          <button class="link-btn" type="button" @click="loadProgram">
            다시 불러오기
          </button>
        </p>
        <template v-else>
          <p class="editor-status">
            {{
              form.exists
                ? `마지막 저장 ${savedAtText || "-"}`
                : "아직 쓰지 않은 프로그램이에요."
            }}
          </p>

          <label class="field field--stack">
            <span class="label">안내 문장 <em class="required">필수</em></span>
            <input
              v-model="form.headline"
              class="input"
              type="text"
              name="program-headline"
              placeholder="예: 오늘은 당기는 힘과 코어를 함께 키웠어요."
              :disabled="saving"
            />
            <span
              class="counter"
              :class="{ 'counter--over': headlineCount > headlineMax }"
              >{{ headlineCount }} / {{ headlineMax }}</span
            >
          </label>
          <p class="field-hint">
            회원 앱 일간 리포트 맨 위에 쓴 그대로 보여요.
          </p>

          <label class="field field--stack">
            <span class="label">프로그램 내용</span>
            <textarea
              v-model="form.content"
              class="input textarea"
              name="program-content"
              rows="10"
              placeholder="운동 순서·세트·시간 등을 자유롭게 써 주세요."
              :disabled="saving"
            ></textarea>
            <span
              class="counter"
              :class="{ 'counter--over': contentCount > contentMax }"
              >{{ contentCount }} / {{ contentMax }}</span
            >
          </label>
          <p class="field-hint">
            센터 기록용이에요. 회원 앱에는 보이지 않고, 맨 위에는 안내 문장만
            보여요.
          </p>

          <!-- 포커스 두 칸(사장님 결정 16, R10): 카디오 포커스(필수, 간결 총평의 비슷한 운동 비교 대상) · 웨이트 포커스(선택, 이름은 총평 문장에 쓰지 않음. 일간 판 6은 근력 위주 예외를 정하는 데 씀, DECISIONS DS6-1) -->
          <label class="field field--stack">
            <span class="label"
              >카디오 포커스 <em class="required">필수</em></span
            >
            <select
              v-model="form.cardioFocus"
              class="input"
              name="program-cardio-focus"
              :disabled="saving"
              :aria-invalid="cardioError ? 'true' : null"
            >
              <option value="" disabled>골라 주세요</option>
              <option
                v-for="f in cardioOptions"
                :key="f.value"
                :value="f.value"
              >
                {{ f.text }}
              </option>
            </select>
          </label>
          <p v-if="cardioError" class="field-error" role="alert">
            {{ cardioError }}
          </p>
          <details class="rule" open>
            <summary class="rule-title">고르는 규칙</summary>
            <ul class="rule-list">
              <li v-for="(line, i) in cardioRule" :key="i">{{ line }}</li>
            </ul>
          </details>

          <label class="field field--stack">
            <span class="label"
              >웨이트 포커스 <em class="optional">선택</em></span
            >
            <select
              v-model="form.weightFocus"
              class="input"
              name="program-weight-focus"
              :disabled="saving"
            >
              <option value="">선택 안 함</option>
              <option
                v-for="f in weightOptions"
                :key="f.value"
                :value="f.value"
              >
                {{ f.text }}
              </option>
            </select>
          </label>
          <p class="field-hint">{{ weightHint }}</p>

          <p v-if="!form.exists" class="field-hint field-hint--accent">
            처음 저장하면 이 날짜·타입에 이미 있는 수업 기록의 오늘의 총평을
            프로그램에 맞춰 한 번 다시 만들어요(기록이 없으면 다시 만들 것이
            없어요). 카디오 포커스는 그때의 값으로 총평의 비슷한 운동 비교에
            쓰여요. 그 뒤에 들어오는 기록은 이 프로그램으로 총평을 만들고,
            고쳐도 이미 만든 총평은 바뀌지 않아요.
          </p>
          <p v-else class="field-hint">
            고쳐도 이미 만든 오늘의 총평은 바뀌지 않아요(포커스를 바꿔도
            같아요). 맨 위 안내는 고친 내용으로 보여요.
          </p>

          <div class="actions">
            <button
              class="save-btn"
              type="button"
              :disabled="!canSave"
              @click="save"
            >
              {{ saving ? "저장 중…" : "저장" }}
            </button>
            <span
              v-if="message"
              class="msg"
              :class="{ 'msg--error': messageError }"
              role="status"
              >{{ message }}</span
            >
            <span v-else-if="dirty && !check.ok" class="msg msg--muted">{{
              check.message
            }}</span>
          </div>

          <!-- 회원 앱 맨 위 미리보기: 안내 문장만(프로그램 내용은 회원 앱에 보이지 않음, 사장님 2026-10-13). 글자 그대로(줄바꿈 유지, HTML로 해석하지 않음) -->
          <section class="preview" aria-label="회원 앱 미리보기">
            <h4 class="preview-title">회원 앱 맨 위 미리보기</h4>
            <p v-if="previewHeadline" class="preview-headline">
              {{ previewHeadline }}
            </p>
            <p v-else class="preview-default">
              {{ defaultSentence }}
              <span class="preview-note">(프로그램을 쓰지 않았을 때)</span>
            </p>
          </section>
        </template>
      </div>
    </aside>
  </div>
</template>

<script>
import {
  CARDIO_FOCUSES,
  CARDIO_FOCUS_RULE,
  CLASS_TYPES,
  LEGACY_CLASS_TYPES,
  WEIGHT_FOCUSES,
  WEIGHT_FOCUS_HINT,
  cardioFocusLabel,
  cardioFocusOptionLabel,
  classTypeLabel,
  classTypeTone,
  weightFocusOptionLabel,
} from "@/lib/classTypes";
import {
  CONTENT_MAX,
  HEADLINE_MAX,
  addDaysYmd,
  charCount,
  defaultHeadline,
  emptyProgramForm,
  firstSaveConfirmText,
  formatSavedAt,
  formatWeekRange,
  hasProgramChanges,
  isYmd,
  missingCount,
  programFormFrom,
  programRequestBody,
  programWeekRows,
  saveResultText,
  validateProgramForm,
  weekDates,
} from "@/lib/dailyPrograms";
import {
  describeProgramLoadError,
  describeProgramSaveError,
  fetchSchedule,
  getDailyProgram,
  listDailyPrograms,
  saveDailyProgram,
} from "@/lib/classAdminApi";

// 주간 목록 타입 표: 새 9개 + 예전 3개(예전 타입 이름·순서를 보이려고). 프로그램은 새 9개 타입에만 쓴다: 서버 PUT이
// 9개만 받는다(422). 예전 타입 + 새 타입 이름 제목(D22) 수업은 목록에서 그 새 타입으로 세고(effectiveTypeOf), 회원 앱도
// 그 새 타입 프로그램을 읽는다(서버 EffectiveClassType, DECISIONS R9-6)
const PROGRAM_TYPES = [...CLASS_TYPES, ...LEGACY_CLASS_TYPES];

function localYmd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default {
  name: "DailyProgramsView",
  // 저장하지 않은 내용이 있으면 다른 메뉴로 옮기기 전에 묻는다
  beforeRouteLeave() {
    if (this.dirty && !this.saving) {
      return window.confirm("저장하지 않은 내용이 있어요. 버리고 옮길까요?");
    }
    return true;
  },
  data() {
    const today = localYmd(new Date());
    return {
      today,
      // 주간 목록
      anchor: today, // 보고 있는 주에 든 날짜
      weekDays: weekDates(today),
      weekItems: [],
      classesByDate: {},
      classesPartial: false,
      weekLoading: false,
      weekError: "",
      weekSeq: 0,
      // 쓰기
      form: emptyProgramForm(today, ""),
      saved: emptyProgramForm(today, ""), // 마지막으로 불러오거나 저장한 값(바뀐 것이 있는지 비교)
      loadingProgram: false,
      programLoadError: "",
      programSeq: 0,
      saving: false,
      message: "",
      messageError: false,
    };
  },
  computed: {
    weekRangeText() {
      return formatWeekRange(this.weekDays);
    },
    weekRows() {
      return programWeekRows(
        this.weekDays,
        this.weekItems,
        this.classesByDate,
        PROGRAM_TYPES
      );
    },
    missing() {
      return missingCount(this.weekRows);
    },
    // 새 9개. 지금 고른 타입이 예전 타입이면 그 하나를 뒤에 더한다
    typeOptions() {
      const list = CLASS_TYPES.map((t) => ({ value: t.value, text: t.label }));
      const legacy = LEGACY_CLASS_TYPES.find((t) => t.value === this.form.type);
      if (legacy) {
        list.push({ value: legacy.value, text: `${legacy.label} (예전 타입)` });
      }
      return list;
    },
    // 카디오 포커스 선택지 "무산소 역치 · 쉬지 않고 6분 넘게 이어 감"(enum 순서 = 고르는 규칙 ①~⑤)
    cardioOptions() {
      return CARDIO_FOCUSES.map((f) => ({
        value: f.value,
        text: cardioFocusOptionLabel(f.value),
      }));
    },
    cardioRule() {
      return CARDIO_FOCUS_RULE;
    },
    // 웨이트 포커스 선택지 "스트렝스 · 무거운 무게 · 적은 반복"
    weightOptions() {
      return WEIGHT_FOCUSES.map((f) => ({
        value: f.value,
        text: weightFocusOptionLabel(f.value),
      }));
    },
    weightHint() {
      return WEIGHT_FOCUS_HINT;
    },
    // 카디오 포커스 칸 아래 붉은 글자. 무엇을 고치기 시작한 뒤(dirty)에만 보인다: 막 연 빈 폼부터 붉게 보이지 않게(저장 줄의 안내와 같은 때)
    cardioError() {
      return this.dirty ? this.check.errors.cardioFocus || "" : "";
    },
    headlineMax() {
      return HEADLINE_MAX;
    },
    contentMax() {
      return CONTENT_MAX;
    },
    headlineCount() {
      return charCount(String(this.form.headline || "").trim());
    },
    contentCount() {
      return charCount(this.form.content);
    },
    check() {
      return validateProgramForm(this.form, CLASS_TYPES);
    },
    dirty() {
      return hasProgramChanges(this.saved, this.form);
    },
    canSave() {
      return (
        !this.saving &&
        !this.loadingProgram &&
        !this.programLoadError &&
        this.check.ok &&
        this.dirty
      );
    },
    savedAtText() {
      return formatSavedAt(this.form.updatedAt);
    },
    previewHeadline() {
      return String(this.form.headline || "").trim();
    },
    defaultSentence() {
      return defaultHeadline(classTypeLabel(this.form.type));
    },
  },
  watch: {
    // 저장한 뒤 다시 고치기 시작하면 저장 결과 문구를 지운다(그 자리에 길이·필수 안내가 보이게)
    dirty(value) {
      if (value && !this.saving) {
        this.message = "";
        this.messageError = false;
      }
    },
  },
  created() {
    // 수업관리의 "운동 프로그램 →"(?date=&type=)로 오면 그 날짜·타입을 바로 연다
    const q = this.$route?.query || {};
    const date = isYmd(q.date) ? String(q.date) : this.today;
    const type = CLASS_TYPES.some((t) => t.value === q.type)
      ? String(q.type)
      : "";
    this.anchor = date;
    this.weekDays = weekDates(date);
    this.loadWeek();
    this.select(date, type);
  },
  methods: {
    toneOf(type) {
      return classTypeTone(type);
    },
    cardioLabelOf(value) {
      return cardioFocusLabel(value);
    },
    isSelected(date, type) {
      return this.form.date === date && this.form.type === type;
    },
    // 저장하지 않은 내용을 버려도 되는가
    confirmDiscard() {
      if (!this.dirty) return true;
      return window.confirm("저장하지 않은 내용이 있어요. 버리고 옮길까요?");
    },
    moveWeek(n) {
      this.anchor = addDaysYmd(this.anchor, 7 * n) || this.anchor;
      this.weekDays = weekDates(this.anchor);
      this.loadWeek();
    },
    pickEntry(date, type) {
      if (this.saving || this.isSelected(date, type)) return;
      if (!this.confirmDiscard()) return;
      this.select(date, type);
    },
    onPickDate(event) {
      const value = event.target.value;
      if (value === this.form.date) return;
      if (!this.confirmDiscard()) {
        event.target.value = this.form.date;
        return;
      }
      this.select(value, this.form.type);
      // 다른 주의 날짜를 고르면 목록도 그 주로 옮긴다
      if (isYmd(value) && !this.weekDays.includes(value)) {
        this.anchor = value;
        this.weekDays = weekDates(value);
        this.loadWeek();
      }
    },
    onPickType(event) {
      const value = event.target.value;
      if (value === this.form.type) return;
      if (!this.confirmDiscard()) {
        event.target.value = this.form.type;
        return;
      }
      this.select(this.form.date, value);
    },
    // 날짜·타입을 고른다. 둘 다 있으면 서버에서 불러온다(없으면 404 → 빈 폼)
    select(date, type) {
      const f = emptyProgramForm(date, type);
      this.form = f;
      this.saved = { ...f };
      this.message = "";
      this.messageError = false;
      this.programLoadError = "";
      if (isYmd(f.date) && f.type) {
        this.loadProgram();
      } else {
        this.programSeq++;
        this.loadingProgram = false;
      }
    },
    async loadProgram() {
      const { date, type } = this.form;
      if (!isYmd(date) || !type) return;
      const seq = ++this.programSeq;
      this.loadingProgram = true;
      this.programLoadError = "";
      try {
        const item = await getDailyProgram(date, type);
        if (seq !== this.programSeq) return;
        const f = programFormFrom(item, date, type);
        this.form = f;
        this.saved = { ...f };
      } catch (e) {
        if (seq !== this.programSeq) return;
        console.error("운동 프로그램 불러오기 실패:", e);
        this.programLoadError = describeProgramLoadError(e);
      } finally {
        if (seq === this.programSeq) this.loadingProgram = false;
      }
    },
    // 그 주의 프로그램 목록과 날짜별 수업(안 쓴 날짜·타입 표시용)을 함께 받는다. 수업 목록이 일부 실패해도 프로그램은 보인다
    async loadWeek() {
      const seq = ++this.weekSeq;
      const dates = weekDates(this.anchor);
      this.weekLoading = true;
      this.weekError = "";
      const [itemsRes, ...scheduleRes] = await Promise.allSettled([
        listDailyPrograms(dates[0], dates[dates.length - 1]),
        ...dates.map((d) => fetchSchedule(d)),
      ]);
      if (seq !== this.weekSeq) return;
      if (itemsRes.status === "fulfilled") {
        this.weekItems = itemsRes.value;
      } else {
        console.error("운동 프로그램 목록 실패:", itemsRes.reason);
        this.weekItems = [];
        this.weekError = describeProgramLoadError(itemsRes.reason);
      }
      const byDate = {};
      let partial = false;
      scheduleRes.forEach((r, i) => {
        if (r.status === "fulfilled") byDate[dates[i]] = r.value;
        else partial = true;
      });
      this.classesByDate = byDate;
      this.classesPartial = partial;
      this.weekDays = dates;
      this.weekLoading = false;
    },
    async save() {
      if (!this.canSave) return;
      // 처음 저장(201)만 그 날짜·타입 기록의 총평·사실을 이 카디오 포커스로 한 번 다시 만들고 그 뒤 고쳐도 바뀌지 않아서
      // 저장 전에 묻는다(DECISIONS R10-6). 고침(이미 있는 프로그램)은 묻지 않는다
      if (
        !this.form.exists &&
        !window.confirm(
          firstSaveConfirmText(cardioFocusLabel(this.form.cardioFocus))
        )
      ) {
        return;
      }
      const { date, type } = this.form;
      const body = programRequestBody(this.form);
      this.saving = true;
      this.message = "";
      this.messageError = false;
      try {
        const { item, created, regenerated } = await saveDailyProgram(
          date,
          type,
          body
        );
        // 저장 중에는 날짜·타입을 바꿀 수 없어 폼은 그대로 이 날짜·타입이다
        const f = programFormFrom(
          item && typeof item === "object" && item.headline
            ? item
            : { date, type, ...body, updated_at: new Date().toISOString() },
          date,
          type
        );
        this.form = f;
        this.saved = { ...f };
        this.message = saveResultText(created, regenerated);
        this.loadWeek();
      } catch (e) {
        console.error("운동 프로그램 저장 실패:", e);
        this.messageError = true;
        this.message = describeProgramSaveError(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* 메인: 왼쪽(주간 목록) + 오른쪽(쓰기) 2열. 수업관리 화면과 같은 틀 */
.programs-content {
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 24px;
  align-items: start;
  min-width: 0;
}

.panel {
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  min-width: 0;
}

.panel-head {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.panel-head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
}

.panel-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8e8e8e;
}

.week-navigation {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.nav-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.nav-btn:hover:not(:disabled) {
  background: #252728;
  border-color: #444;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.current-week {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  min-width: 140px;
  text-align: center;
}

.week-msg {
  margin: 0;
  font-size: 13px;
  color: #c8c8c8;
}

.week-msg--error {
  color: #ef4444;
}

.link-btn {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #78ff47;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

.empty {
  padding: 20px 8px;
  color: #c8c8c8;
}

.days {
  display: grid;
  gap: 12px;
}

.day-card {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 12px;
  min-width: 0;
}

.day-card--today {
  border: 1px solid rgba(120, 255, 71, 0.4);
}

.day-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

.day-empty {
  margin: 0;
  font-size: 13px;
  color: #6b6b6b;
}

.entries {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.entry {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: #151718;
  border: 1px solid #262829;
  border-radius: 8px;
  color: #fff;
  text-align: left;
  cursor: pointer;
  min-width: 0;
}

.entry:hover {
  background: #1a1c1d;
}

.entry.selected {
  border: 2px solid #78ff47;
}

.entry--missing {
  border-style: dashed;
}

/* 예전 타입 수업만 있는 줄: 프로그램을 쓸 수 없어 누르지 않는다 */
.entry--legacy {
  cursor: default;
  opacity: 0.7;
}

.entry-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  min-width: 0;
}

.entry-name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.entry-type {
  font-size: 15px;
  font-weight: 600;
}

.entry-focus {
  font-size: 14px;
  color: #c8c8c8;
}

/* 카디오 포커스가 없는 쓴 프로그램(R10 전 개발 DB 행) */
.entry-focus--none {
  color: #6b6b6b;
}

.tone-hyb {
  color: #49de80;
}
.tone-core {
  color: #f59e0c;
}
.tone-car {
  color: #ef4444;
}

.entry-count {
  font-size: 12px;
  color: #8e8e8e;
}

.chip {
  margin-left: auto;
  height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  border-radius: 5px;
  font-size: 12px;
  white-space: nowrap;
}

.chip--done {
  border: 1px solid #78ff47;
  color: #78ff47;
}

.chip--missing {
  border: 1px dashed #ff817f;
  color: #ff817f;
}

.chip--legacy {
  border: 1px solid #5a5a5a;
  color: #8e8e8e;
}

.entry-headline {
  font-size: 13px;
  color: #c8c8c8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 오른쪽: 쓰기 */
.right {
  width: 420px;
  min-width: 420px;
  align-self: start;
}

.editor {
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.panel-title {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

.pickers {
  display: grid;
  gap: 10px;
}

.field {
  display: grid;
  grid-template-columns: 56px 1fr;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.field--stack {
  grid-template-columns: 1fr;
  gap: 6px;
}

.label {
  font-size: 14px;
  color: #c8c8c8;
}

.required {
  margin-left: 4px;
  font-style: normal;
  font-size: 12px;
  color: #ff817f;
}

.optional {
  margin-left: 4px;
  font-style: normal;
  font-size: 12px;
  color: #8e8e8e;
}

.input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px;
  background: #2a2a2a;
  border: 1px solid #555;
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color-scheme: dark;
}

.input:disabled {
  opacity: 0.6;
}

.textarea {
  resize: vertical;
  min-height: 160px;
  line-height: 1.5;
}

.counter {
  justify-self: end;
  font-size: 12px;
  color: #8e8e8e;
}

.counter--over {
  color: #ef4444;
}

.field-hint {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8e8e8e;
}

.field-hint--accent {
  color: #78ff47;
}

.field-error {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #ef4444;
}

/* 카디오 포커스 고르는 규칙(사장님 결정 16 문구) */
.rule {
  margin: -4px 0 0;
  padding: 8px 10px;
  border: 1px solid #262829;
  border-radius: 8px;
  background: #0a0a0a;
}

.rule-title {
  font-size: 12px;
  color: #c8c8c8;
  cursor: pointer;
}

.rule-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: grid;
  gap: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #8e8e8e;
  overflow-wrap: anywhere;
}

.editor-status {
  margin: 0;
  font-size: 13px;
  color: #8e8e8e;
}

.editor-empty {
  margin: 0;
  padding: 12px 0;
  font-size: 14px;
  color: #c8c8c8;
}

.editor-empty--error {
  color: #ef4444;
}

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.save-btn {
  height: 36px;
  padding: 0 20px;
  border: 0;
  border-radius: 5px;
  background: #78ff47;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.save-btn:disabled {
  background: #3a3c3d;
  color: #8e8e8e;
  cursor: not-allowed;
}

.msg {
  font-size: 13px;
  line-height: 1.4;
  color: #78ff47;
  flex: 1 1 200px;
}

.msg--error {
  color: #ef4444;
}

.msg--muted {
  color: #8e8e8e;
}

.preview {
  margin-top: 4px;
  padding: 12px;
  border: 1px solid #262829;
  border-radius: 8px;
  background: #0a0a0a;
}

.preview-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 500;
  color: #8e8e8e;
}

/* 글자 그대로: 줄바꿈과 공백을 살리고 긴 낱말은 줄을 바꾼다 */
.preview-headline,
.preview-default {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: #fff;
}

.preview-headline {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.preview-default {
  font-size: 15px;
  color: #c8c8c8;
}

.preview-note {
  font-size: 12px;
  color: #6b6b6b;
}

/* 1100px 이하: 1열 */
@media (max-width: 1100px) {
  .programs-content {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .right {
    width: auto;
    min-width: 0;
  }
}

@media (max-width: 720px) {
  .panel-head h2,
  .panel-title {
    font-size: 20px;
  }
}
</style>
